import React from 'react';

import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { Chip, IconButton, Stack, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { Theme, alpha } from '@mui/material/styles';

import PermissionedComponent from '~/components/Common/PermissionedComponent';

import { AVAILABILITY, ONLINE_PURCHASE } from '~/consts/schedule';

import { isBetween } from '~/services/TimeService';

import CampaignScheduleDateEntryPill from './CampaignScheduleDateEntryPill';
import { CampaignScheduleDropdownRow } from './CampaignScheduleExpandableRow';
import { CUSTOMER_FACING_TYPES } from './campaignScheduleBrandRows';
import { BRAND_ROWS_TABLE_COLUMN_COUNT, handleExpandRowKeyDown } from './campaignScheduleTabLayout';
import type { CampaignScheduleBrandRow as BrandRow, CampaignScheduleDateEntry } from './types';

// restrict_purchase_to_lux_plus_members is nullable; null reads as not-restricted (same as false).
function allRestricted(row: BrandRow): boolean {
  return row.schedules.every((s) => !!s.restrict_purchase_to_lux_plus_members);
}

function someRestricted(row: BrandRow): boolean {
  return row.schedules.some((s) => !!s.restrict_purchase_to_lux_plus_members) && !allRestricted(row);
}

type RowStatus = 'active' | 'availableOffline' | 'availableForStaff' | 'inactive';

const ROW_STATUS_CONFIG: Record<RowStatus, { label: string; color: 'success' | 'warning' | 'info' | undefined }> = {
  active: { label: 'Active', color: 'success' },
  availableOffline: { label: 'Available Offline', color: 'warning' },
  availableForStaff: { label: 'Available for Staff', color: 'info' },
  inactive: { label: 'Inactive', color: undefined },
};

function getRowStatusColor(status: RowStatus, theme: Theme) {
  switch (status) {
    case 'active':
      return theme.palette.success.main;
    case 'availableOffline':
      return theme.palette.warning.main;
    case 'availableForStaff':
      return theme.palette.info.main;
    case 'inactive':
      return theme.palette.grey[500];
  }
}

// Priority: list_visibility/lux_plus (search-visible) > online_purchase (direct-link only) >
// availability (staff-only) — a lingering availability/online_purchase window alone never counts as Active.
function getRowStatus(dateEntries: Array<CampaignScheduleDateEntry>): RowStatus {
  const now = new Date();
  const activeTypes = new Set(
    dateEntries.filter((entry) => isBetween(entry.start, entry.end, now, undefined, '[]')).map((entry) => entry.type),
  );

  if (CUSTOMER_FACING_TYPES.some((type) => activeTypes.has(type))) {
    return 'active';
  }
  if (activeTypes.has(ONLINE_PURCHASE)) {
    return 'availableOffline';
  }
  if (activeTypes.has(AVAILABILITY)) {
    return 'availableForStaff';
  }
  return 'inactive';
}

interface Props {
  row: BrandRow;
  offerId: string | undefined;
  isExpanded: boolean;
  isTogglingRestriction: boolean;
  onToggleExpand: () => void;
  onManage: () => void;
  onToggleRestriction: (restrict: boolean) => void;
}

export default function CampaignScheduleBrandRow({
  row,
  offerId,
  isExpanded,
  isTogglingRestriction,
  onToggleExpand,
  onManage,
  onToggleRestriction,
}: Props) {
  const status = getRowStatus(row.dateEntries);
  const statusConfig = ROW_STATUS_CONFIG[status];

  return (
    <>
      <TableRow>
        <TableCell>
          <Typography fontWeight={700} variant="body2">
            {row.brand}
          </Typography>
        </TableCell>
        <TableCell>
          <Chip
            color={row.isLuxPlus ? undefined : 'info'}
            label={row.isLuxPlus ? 'LuxPlus' : 'Normal'}
            size="small"
            sx={(theme) => ({
              bgcolor: alpha(row.isLuxPlus ? theme.palette.luxPlus.main : theme.palette.info.main, 0.12),
              color: row.isLuxPlus ? theme.palette.luxPlus.main : undefined,
              borderColor: row.isLuxPlus ? theme.palette.luxPlus.main : undefined,
              fontWeight: 600,
            })}
            variant="outlined"
          />
        </TableCell>
        <TableCell>
          <Chip
            color={statusConfig.color}
            label={statusConfig.label}
            size="small"
            sx={{
              bgcolor: (theme) => alpha(getRowStatusColor(status, theme), 0.12),
              fontWeight: 600,
            }}
            variant="outlined"
          />
        </TableCell>
        <TableCell>
          <Stack spacing={1}>
            {row.dateEntries.map((entry) => (
              <CampaignScheduleDateEntryPill key={entry.type} entry={entry} />
            ))}
          </Stack>
        </TableCell>
        <TableCell>
          <Stack
            alignItems="center"
            direction="row"
            role="button"
            spacing={0.5}
            sx={{ cursor: 'pointer' }}
            tabIndex={0}
            onClick={onToggleExpand}
            onKeyDown={(event) => handleExpandRowKeyDown(event, onToggleExpand)}
          >
            <Typography variant="body2">
              {row.regions.length === 0
                ? 'No regions'
                : row.regions.length === 1
                  ? row.regions[0]
                  : `${row.regions.length} regions`}
            </Typography>
            {isExpanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
          </Stack>
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={0.5}>
            <PermissionedComponent>
              <Tooltip title={`Edit ${row.brand} schedules`}>
                {/* Tooltip needs a non-disabled child to attach its listeners to — wrapping in a
                    span keeps the tooltip working (and explains why) even while the button itself
                    is disabled. See https://mui.com/material-ui/react-tooltip/#disabled-elements */}
                <span>
                  <IconButton
                    aria-label={`Edit ${row.brand} schedules`}
                    disabled={!offerId}
                    size="small"
                    onClick={onManage}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </PermissionedComponent>
            {row.isLuxPlus && row.schedules.length > 0 && (
              <PermissionedComponent>
                <Tooltip
                  title={
                    allRestricted(row)
                      ? `Remove LuxPlus+ restriction from all ${row.brand} schedules`
                      : someRestricted(row)
                        ? `Some ${row.brand} schedules are LuxPlus+ restricted — restrict all`
                        : `Restrict all ${row.brand} schedules to LuxPlus+ members`
                  }
                >
                  <span>
                    <IconButton
                      aria-label={`Toggle LuxPlus+ restriction for all ${row.brand} schedules`}
                      disabled={!offerId || isTogglingRestriction}
                      size="small"
                      onClick={() => onToggleRestriction(!allRestricted(row))}
                    >
                      {allRestricted(row) ? (
                        <LockIcon color="error" fontSize="small" />
                      ) : someRestricted(row) ? (
                        <LockIcon color="warning" fontSize="small" />
                      ) : (
                        <LockOpenIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </PermissionedComponent>
            )}
          </Stack>
        </TableCell>
      </TableRow>
      <CampaignScheduleDropdownRow colSpan={BRAND_ROWS_TABLE_COLUMN_COUNT} isExpanded={isExpanded}>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {row.regions.map((region) => (
            <Chip key={region} label={region} size="small" variant="outlined" />
          ))}
        </Stack>
      </CampaignScheduleDropdownRow>
    </>
  );
}
